class InfiniteScrollComponent extends HTMLElement {
  constructor() {
    super();
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMorePages = true;
    this.scrollSaveTimer = null;
    this.isPopstateNavigation = false;
    this.scrollRestoreDelay = 100;

    // Default options
    this.options = {
      productsContainer: ".collection_products_list",
      paginationSelector: ".pagination",
      autoLoad: true,
      loadMoreText: "Load More Products",
      scrollThreshold: 0,
      storagePrefix: "InfiniteScroll_",
      scrollSaveDelay: 150,
      loadingImageUrl: null,
      productCountSelector: "#ProductCount",
    };
  }

  connectedCallback() {
    if (!window.location.pathname.includes("/collections/")) return;
    // Get options from attributes
    this.parseAttributes();
    // Initialize component
    this.init();

    console.log("InfiniteScrollComponent connected");
  }

  parseAttributes() {
    if (this.hasAttribute("auto-load")) {
      this.options.autoLoad = this.getAttribute("auto-load") !== "false";
    }
    if (this.hasAttribute("load-more-text")) {
      this.options.loadMoreText = this.getAttribute("load-more-text");
    }
    if (this.hasAttribute("scroll-threshold")) {
      this.options.scrollThreshold = parseInt(this.getAttribute("scroll-threshold")) || 200;
    }
    if (this.hasAttribute("products-container")) {
      this.options.productsContainer = this.getAttribute("products-container");
    }
    if (this.hasAttribute("loading-image")) {
      this.options.loadingImageUrl = this.getAttribute("loading-image");
    }
    if (this.hasAttribute("product-count-selector")) {
      this.options.productCountSelector = this.getAttribute("product-count-selector");
    }
  }

  init() {
    this.productsContainer = document.querySelector(this.options.productsContainer);
    this.pagination = document.querySelector(this.options.paginationSelector);

    if (!this.productsContainer) {
      console.warn("Products container not found");
      return;
    }
    // Parse current page from URL
    this.parseCurrentPageFromUrl();
    // Check if URL changed and clear cache if needed
    this.checkUrlChange();
    // Restore cached data if available
    this.restoreFromCache();
    // Setup component
    this.setupPagination();
    this.bindEvents();

    // On popstate (back/forward), browser handles scroll restoration natively
    // so we skip our manual restoration to avoid conflicts
    if (!this.isPopstateNavigation) {
      this.restoreScrollPosition();
    }
  }

  /**
   * Parse the current page from the URL
   * If the page param is greater than the current page, clear the cache
   */
  parseCurrentPageFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get("page");
    console.log("Page param:", pageParam);
    if (pageParam) {
      const pageNum = parseInt(pageParam);
      if (!isNaN(pageNum) && pageNum > 0) {
        if (pageNum >= (this.getCachedData()?.currentPage || 1)) {
          this.clearCache();
        }
        this.currentPage = pageNum;
        this.saveToCache();
      }
    }
  }

  /**
   * Check if the URL has changed
   * If the URL has changed, clear the cache
   */
  checkUrlChange() {
    const currentUrl = window.location.href;
    const cachedData = this.getCachedData();

    if (cachedData && cachedData.url !== currentUrl) {
      this.clearCache();
    }
  }

  /**
   * Setup the pagination
   * If the autoLoad option is false, create a load more button
   * If the loadingImageUrl option is provided, create a loading image
   */
  setupPagination() {
    if (this.pagination) {
      this.pagination.style.display = "none";
      if (!this.options.autoLoad) {
        this.createLoadMoreButton();
      }

      // Create loading image if URL provided
      if (this.options.loadingImageUrl) {
        this.createLoadingImage();
      }
    }
  }

  /**
   * Create a load more button
   */
  createLoadMoreButton() {
    if (this.loadMoreBtn) return;

    this.loadMoreBtn = document.createElement("button");
    this.loadMoreBtn.className = "infinite-scroll-load-more";
    this.loadMoreBtn.textContent = this.options.loadMoreText;
    this.productsContainer.insertAdjacentElement("afterend", this.loadMoreBtn);
    this.loadMoreBtn.addEventListener("click", () => this.loadNextPage());
  }

  /**
   * Create a loading image
   */
  createLoadingImage() {
    if (this.loadingImg) return;

    this.loadingImg = document.createElement("img");
    this.loadingImg.src = this.options.loadingImageUrl;
    this.loadingImg.alt = "Loading image";
    this.loadingImg.className = "infinite-scroll-loading-image";
    this.loadingImg.style.visibility = "hidden";

    // Insert the loading image after the load more button if it exists,
    // otherwise after the products container
    if (this.loadMoreBtn) {
      this.loadMoreBtn.insertAdjacentElement("afterend", this.loadingImg);
    } else {
      this.productsContainer.insertAdjacentElement("afterend", this.loadingImg);
    }
    if (!this.hasMorePages) {
      this.loadingImg.style.display = "none";
    }
  }

  bindEvents() {
    // store the handlers
    this.boundCheckScroll = this.throttle(this.checkScroll, 100);
    this.boundSaveScroll = this.saveScrollPosition.bind(this);
    this.boundPopState = this.handlePopState.bind(this);

    if (this.options.autoLoad) {
      window.addEventListener("scroll", this.boundCheckScroll);
    }
    window.addEventListener("scroll", this.boundSaveScroll);
    window.addEventListener("popstate", this.boundPopState);
  }

  saveScrollPosition() {
    if (this.scrollSaveTimer) {
      clearTimeout(this.scrollSaveTimer);
    }

    this.scrollSaveTimer = setTimeout(() => {
      const cachedData = this.getCachedData() || {};
      // Capture the scroll position at save time
      cachedData.scrollY = window.scrollY;
      this.setCachedData(cachedData);
      this.scrollSaveTimer = null;
    }, this.options.scrollSaveDelay);
  }

  restoreScrollPosition() {
    const cachedData = this.getCachedData();
    if (!cachedData || typeof cachedData.scrollY !== "number") {
      return;
    }

    setTimeout(() => {
      window.scrollTo(0, cachedData.scrollY);
    }, this.scrollRestoreDelay);
  }

  handlePopState() {
    // Only handle popstate on mobile (width <= 768px)
    if (window.innerWidth > 768) return;
    this.isPopstateNavigation = true;
    setTimeout(() => {
      this.checkUrlChange();
      // Reset the flag after handling popstate navigation
      this.isPopstateNavigation = false;
    }, this.scrollRestoreDelay);
  }

  /**
   * Check if the scroll position is at the bottom of the products container
   */
  checkScroll() {
    if (this.isLoading || !this.hasMorePages) return;

    const gridBottom = this.productsContainer.getBoundingClientRect().bottom;
    const viewportBottom = window.innerHeight;

    if (gridBottom - this.options.scrollThreshold <= viewportBottom) {
      this.loadNextPage();
    }
  }
  /**
   * Load the next page by fetching the next page URL and appending it to the products container
   */
  async loadNextPage() {
    if (this.isLoading || !this.hasMorePages) return;

    this.isLoading = true;
    this.updateLoadingState(true);

    try {
      const nextPage = this.currentPage + 1;
      console.log("Loading next page:", nextPage);
      const url = this.buildNextPageUrl(nextPage);
      const response = await fetch(url, { credentials: "same-origin" });

      if (!response.ok) {
        console.warn("Failed to load next page:", response.status);
        this.handleEndOfResults();
        return;
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Check if the response has products before incrementing page
      const newProductsContainer = doc.querySelector(this.options.productsContainer);

      if (!newProductsContainer || !newProductsContainer.children.length) {
        this.handleEndOfResults();
        return;
      }

      this.currentPage = nextPage;
      this.processResponse(doc);
      this.saveToCache();
    } catch (err) {
      console.error("Error loading next page:", err);
      this.handleEndOfResults();
    } finally {
      this.isLoading = false;
      this.updateLoadingState(false);
    }
  }

  buildNextPageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set("page", page);
    return url.toString();
  }

  processResponse(doc) {
    const newProductsContainer = doc.querySelector(this.options.productsContainer);

    if (!newProductsContainer || !newProductsContainer.children.length) {
      this.handleEndOfResults();
      return;
    }

    const newProducts = Array.from(newProductsContainer.children);
    newProducts.forEach((product) => {
      this.productsContainer.appendChild(product.cloneNode(true));
    });

    // Check if there are more pages using multiple methods
    this.hasMorePages = this.checkHasMorePages(doc);

    if (!this.hasMorePages) {
      this.handleEndOfResults();
    }
  }

  /**
   * Check if there are more pages by checking the new document
   */
  checkHasMorePages(doc) {
    // Method 1: Check if the fetched page has any products at all
    const newProductsContainer = doc.querySelector(this.options.productsContainer);
    if (!newProductsContainer || newProductsContainer.children.length === 0) {
      return false;
    }

    // Method 2: Check for next page link in the new document
    const nextPageLink = doc.querySelector(`a[href*="page=${this.currentPage + 1}"]`);
    if (nextPageLink) return true;

    // Method 3: Check pagination in the new document for any "next" link
    const paginationNext = doc.querySelector('.pagination a[rel="next"], .pagination .next:not(.disabled)');
    if (paginationNext) return true;

    // Method 4: Check ProductCount element to see if we've loaded all products
    const productCountElement = document.querySelector(this.options.productCountSelector);
    if (productCountElement) {
      const totalProducts = parseInt(productCountElement.textContent) || 0;
      const currentProductsLoaded = this.productsContainer.children.length;

      if (currentProductsLoaded >= totalProducts) {
        return false; // We've loaded all products
      }
    }
    // Default to false if we can't determine definitively
    return false;
  }

  updateLoadingState(isLoading) {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.style.display = isLoading ? "none" : "block";
      this.loadMoreBtn.disabled = isLoading;
    }

    // Show/hide loading image
    if (this.loadingImg) {
      this.loadingImg.style.visibility = isLoading ? "visible" : "hidden";
    }
  }

  handleEndOfResults() {
    this.hasMorePages = false;
    if (this.loadMoreBtn) {
      this.loadMoreBtn.remove();
      this.loadMoreBtn = null;
    }

    if (this.loadingImg) {
      this.loadingImg.remove();
      this.loadingImg = null;
    }
  }

  // Cache management
  getCacheKey() {
    return this.options.storagePrefix + "data";
  }

  getCachedData() {
    try {
      const data = sessionStorage.getItem(this.getCacheKey());
      return data ? JSON.parse(data) : null;
    } catch (err) {
      console.warn("Failed to parse cached data:", err);
      return null;
    }
  }

  setCachedData(data) {
    try {
      sessionStorage.setItem(this.getCacheKey(), JSON.stringify(data));
    } catch (err) {
      console.warn("Failed to cache data:", err);
    }
  }

  saveToCache() {
    const data = {
      html: this.productsContainer.innerHTML,
      currentPage: this.currentPage,
      hasMorePages: this.hasMorePages,
      url: window.location.href,
      scrollY: window.scrollY,
    };
    this.setCachedData(data);
  }

  restoreFromCache() {
    const cachedData = this.getCachedData();

    if (!cachedData) return;

    // Check if cache is for current URL
    if (cachedData.url !== window.location.href) {
      this.clearCache();
      return;
    }

    // Restore state
    this.productsContainer.innerHTML = cachedData.html;
    this.currentPage = cachedData.currentPage;
    this.hasMorePages = cachedData.hasMorePages;

    // Recreate load more button if needed
    if (!this.options.autoLoad && this.hasMorePages) {
      this.createLoadMoreButton();
    }
  }

  clearCache() {
    try {
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith(this.options.storagePrefix)) {
          sessionStorage.removeItem(key);
        }
      });
      this.resetState();
    } catch (err) {
      console.warn("Failed to clear cache:", err);
    }
  }

  throttle(fn, wait) {
    let lastTime = 0;
    return (...args) => {
      const now = Date.now();
      if (now - lastTime >= wait) {
        lastTime = now;
        fn.apply(this, args);
      }
    };
  }

  resetState() {
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMorePages = true;

    if (this.loadMoreBtn) {
      this.loadMoreBtn.remove();
      this.loadMoreBtn = null;
    }
    if (this.loadingImg) {
      this.loadingImg.remove();
      this.loadingImg = null;
    }
  }

  disconnectedCallback() {
    if (this.boundCheckScroll) window.removeEventListener("scroll", this.boundCheckScroll);
    if (this.boundSaveScroll) window.removeEventListener("scroll", this.boundSaveScroll);
    if (this.boundPopState) window.removeEventListener("popstate", this.boundPopState);
  }
}

// Register the custom element
customElements.define("infinite-scroll", InfiniteScrollComponent);
